import { index, sqliteTable, text } from 'drizzle-orm/sqlite-core'

import { id, timestamps } from './_columns'

/** A registered site: one web + adminweb pair, with its package and how to reach its control API. */
export const sites = sqliteTable(
  'sites',
  {
    id: id(),
    /** Short unique name used in URLs and audit entries. */
    slug: text('slug').notNull().unique(),
    name: text('name').notNull(),
    /** `starter | business | advanced | store`: what the console wants the site to run. */
    package: text('package').notNull().default('advanced'),
    /** `active | suspended`. */
    status: text('status').notNull().default('active'),
    /** Public site and CMS origins (`https://…`, no trailing slash). */
    webUrl: text('web_url').notNull(),
    adminUrl: text('admin_url').notNull(),
    /** The site's CONTROL_SECRET, sealed with the console's key (AES-GCM); never returned by the API. */
    controlSecretSealed: text('control_secret_sealed'),
    /**
     * Suffix of the site's Worker names: `bak-web-<suffix>`, `bak-admin-<suffix>`, `bak-api-<suffix>`. Empty means the
     * slug (customer sites from infra/provision-site.sh); the hand-run production site is `production`.
     */
    workerSuffix: text('worker_suffix'),
    /** Per-site exceptions to the package: `{ flags: {...}, limits: {...} }` (same shape as the site's own). */
    overrides: text('overrides', { mode: 'json' }).$type<{ flags?: Record<string, boolean>; limits?: Record<string, number | null> }>().notNull().default({}),
    /** How the site's public web is built: `enabled` switches the Rebuild button and CMS-triggered builds on. */
    buildConfig: text('build_config', { mode: 'json' }).$type<{ enabled?: boolean }>().notNull().default({}),
    notes: text('notes').notNull().default(''),
    /** Last successful usage read (JSON of the site's `ControlUsage`), when, and the last failure if any. */
    lastUsage: text('last_usage', { mode: 'json' }).$type<unknown>(),
    lastSyncAt: text('last_sync_at'),
    lastSyncError: text('last_sync_error'),
    ...timestamps,
  },
  (t) => [index('ix_sites_status').on(t.status)],
)

export const consoleSettings = sqliteTable('console_settings', {
  key: text('key').primaryKey(),
  value: text('value', { mode: 'json' }).notNull(),
  updatedAt: timestamps.updatedAt,
})

export type SiteRow = typeof sites.$inferSelect
